import type { ComponentKey } from '@/features/dashboard/lib/widget-sizes'
import { BillingFeature } from '@/lib/billing/capabilities'
import store from '@/lib/configs/store-config'
import type { PhaseOneAmberdataAnalyticsType } from '@/service/amberdata/analytics.types'
import { dataWidgetContentAtomFamily } from '@/state/widget-instances'
import { AlgoType } from '@/types/generated/algo'
import { AnalyticsType } from '@/types/generated/common/types'

/** Order types that get a drawn diagram instead of a widget preview image. */
export type DiagramId = 'twap' | 'advanced-twap' | 'limit-chase' | 'rfq' | 'spreader-algo' | 'arb'

/** Full pages with a tour of their own, run in place on the page. */
export type PageTutorialId = 'screener' | 'hedge' | 'assistant' | 'risk-slides-page'

export type TutorialId = ComponentKey | DiagramId | PageTutorialId

/** What a plan must allow for the thing a tutorial explains; absent means ungated. */
export type TutorialGate = { feature: BillingFeature } | { algo: AlgoType }

export interface TutorialStep {
  /**
   * i18n key under `tutorial.items.<id>.steps`, and the `data-tutorial` value of the real
   * element the step points at inside the widget.
   */
  key: string
  /** Fallback dot position, in percent of the preview, while no anchor is found. */
  x: number
  y: number
  /**
   * The setting lives in the widget's ⋯ menu: when no `data-tutorial` element for the step is
   * rendered (the menu is closed), point at the ⋯ button instead of the whole widget.
   */
  inMenu?: boolean
  /** A nearby control to explain an optional feature when its own control is absent. */
  fallbackKey?: string
  /**
   * Sets the live preview up for this step (e.g. switches the Data widget to the dataset the
   * step is about), given the preview's card id. Only the dialog's preview runs it — never a
   * tutorial over the user's own widget.
   */
  onPreview?: (cardId: string) => void
  /**
   * The step asks the user to do something: a selector that matches once they have. Run in
   * place, the card waits (Next reads Skip) and confirms when it matches; its copy then needs
   * `waiting` and `done` lines. The look-only preview just explains.
   */
  waitFor?: string
  /** Move to the next step as soon as this step's `waitFor` selector matches. */
  advanceOnDone?: boolean
}

/** Where a step points: its own control, or for a menu setting, the widget's ⋯ button. */
export function stepAnchorSelectors(step: TutorialStep): string[] {
  const own = `[data-tutorial="${step.key}"]`
  if (step.fallbackKey) return [own, `[data-tutorial="${step.fallbackKey}"]`]
  return step.inMenu ? [own, '[data-widget-menu-trigger]'] : [own]
}

/** The Order Form tab (`OrderFormSummaryTab` values) an order-type preview opens on. */
export type PreviewOrderTab = 'TWAP' | 'Advanced TWAP' | 'Limit Chase' | 'RFQ'

/**
 * What a tutorial renders, live and look-only: a registered widget, the Order Form (on an
 * order-type tab or in spreader mode), or the arb builder.
 */
export type TutorialPreview =
  | { kind: 'widget'; widget: ComponentKey }
  | { kind: 'order-form'; tab?: PreviewOrderTab; spreader?: boolean }
  | { kind: 'arb' }
  // A full page: nothing to preview in a dialog, the tour always runs on the page itself.
  | { kind: 'page' }

/** Width the live preview renders at, in CSS px, before it's scaled to fit the dialog. */
export const PREVIEW_WIDTH = 460

export interface TutorialDefinition {
  id: TutorialId
  /** Static picture, shown only if the live preview fails to render. */
  illustration?: { image: string } | { diagram: DiagramId }
  preview: TutorialPreview
  /** Optional wider render canvas for horizontally dense live previews. */
  previewWidth?: number
  /** Height the live preview renders at (at `previewWidth` or `PREVIEW_WIDTH`), in CSS px. */
  previewHeight: number
  steps: readonly TutorialStep[]
  gate?: TutorialGate
  /** The widget it adds when opened from the Workspace catalogue. */
  widget?: ComponentKey
  /**
   * The page it can run on in place (marked `data-tutorial-page="<id>"`). A page tutorial
   * always goes there; one that also has a preview (Arb) goes there from the hub.
   */
  route?: string
}

/** A page tutorial's root: anchors are looked up on the whole page, this closes it if gone. */
export function tutorialPageSelector(id: TutorialId) {
  return `[data-tutorial-page="${id}"]`
}

const img = (name: string) => ({ image: `/images/examples/dark/${name}.webp` })

/** A Data widget preview step's setup: show `analyticsType`, whose controls the step is about. */
const showDataset = (analyticsType: PhaseOneAmberdataAnalyticsType) => (cardId: string) =>
  store.set(dataWidgetContentAtomFamily(cardId), { kind: 'amberdata', analyticsType })

const ORDER_FORM_HEIGHT = 600

const widget = (
  id: ComponentKey,
  image: string,
  previewHeight: number,
  steps: TutorialStep[],
  gate?: TutorialGate
): TutorialDefinition => ({
  id,
  illustration: img(image),
  preview: id === 'chat-order-form' ? { kind: 'order-form' } : { kind: 'widget', widget: id },
  previewHeight,
  steps,
  gate,
  widget: id,
})

const order = (
  id: DiagramId,
  preview: TutorialPreview,
  steps: TutorialStep[],
  algo: AlgoType
): TutorialDefinition => ({
  id,
  illustration: { diagram: id },
  preview,
  previewHeight: ORDER_FORM_HEIGHT,
  steps,
  gate: { algo },
})

const page = (id: PageTutorialId, route: string, keys: string[]): TutorialDefinition => ({
  id,
  preview: { kind: 'page' },
  previewHeight: 0,
  // x/y place dots on a dialog preview, which a page never has.
  steps: keys.map((key) => ({ key, x: 50, y: 50 })),
  route,
})

/**
 * Every tutorial, by id. Copy lives in `messages/*.json` under `tutorial.items.<id>`
 * (`title`, `summary`, and `steps.<key>.title` / `.body`). Each step's dot lands on the
 * widget's element marked `data-tutorial="<step key>"`; x/y only cover the moment before it
 * renders (or a widget state that hides it).
 */
export const TUTORIALS: Record<TutorialId, TutorialDefinition> = {
  'price-chart': widget('price-chart', 'pie-chart', 360, [
    { key: 'views', x: 86, y: 12 },
    { key: 'indicators', x: 40, y: 52 },
    { key: 'overlays', x: 91, y: 50, inMenu: true },
  ]),
  'order-book': widget('order-book', 'order-book', 420, [
    { key: 'prefill', x: 15, y: 69 },
    { key: 'bars', x: 70, y: 78 },
    { key: 'yourOrders', x: 85, y: 60 },
  ]),
  trades: widget('trades', 'trades', 400, [
    { key: 'prefill', x: 15, y: 43 },
    { key: 'filters', x: 86, y: 12, inMenu: true },
    { key: 'grouping', x: 60, y: 87 },
  ]),
  ticker: widget('ticker', 'ticker', 240, [
    { key: 'summary', x: 20, y: 48 },
    { key: 'change', x: 82, y: 48 },
    { key: 'funding', x: 85, y: 72 },
    { key: 'liquidations', x: 85, y: 72, fallbackKey: 'funding' },
  ]),
  watchlist: widget('watchlist', 'watch-list', 400, [
    { key: 'pick', x: 8, y: 45 },
    { key: 'menu', x: 74, y: 72 },
    { key: 'add', x: 86, y: 12 },
    { key: 'watchlistFilters', x: 86, y: 12, fallbackKey: 'add' },
  ]),
  positions: widget('positions', 'positions-orders-history', 340, [
    { key: 'tabs', x: 60, y: 12 },
    { key: 'expand', x: 50, y: 5 },
    { key: 'actions', x: 13, y: 47 },
    { key: 'edit', x: 13, y: 66 },
    { key: 'group', x: 13, y: 85, inMenu: true },
  ]),
  'chat-order-form': widget('chat-order-form', 'chat-order-form', ORDER_FORM_HEIGHT, [
    { key: 'prompt', x: 30, y: 79 },
    { key: 'orderAccounts', x: 30, y: 28, fallbackKey: 'types' },
    { key: 'types', x: 70, y: 40 },
    { key: 'exits', x: 10, y: 60 },
    { key: 'shortcut', x: 88, y: 79 },
  ]),
  'account-cards': widget('account-cards', 'account-overview', 360, [
    { key: 'accounts', x: 86, y: 12 },
    { key: 'value', x: 25, y: 50 },
    { key: 'metrics', x: 70, y: 86 },
  ]),
  'economic-calendar': widget('economic-calendar', 'economic-calendar', 400, [
    { key: 'mode', x: 86, y: 12 },
    { key: 'events', x: 35, y: 44 },
    { key: 'impact', x: 90, y: 44 },
  ]),
  newsfeed: widget('newsfeed', 'newsfeed', 420, [
    { key: 'coin', x: 19, y: 49 },
    { key: 'filters', x: 86, y: 12 },
    { key: 'read', x: 60, y: 72 },
  ]),
  'risk-slides': widget(
    'risk-slides',
    'risk-slides',
    // Tall: scenario inputs, the portfolio and the results all stack in it.
    620,
    [
      { key: 'setup', x: 12, y: 10 },
      { key: 'build', x: 50, y: 30 },
      { key: 'grid', x: 49, y: 70 },
      { key: 'view', x: 90, y: 78 },
      { key: 'slots', x: 70, y: 30 },
      { key: 'page', x: 30, y: 4 },
    ],
    { feature: BillingFeature.RiskSimulation }
  ),
  // Datasets come in a few shapes, each with its own controls: each step shows one.
  data: widget('data', 'data-chart', 400, [
    {
      key: 'dataset',
      x: 14,
      y: 12,
      onPreview: showDataset(AnalyticsType.ANALYTICS_TYPE_VOL_METRICS_24HR),
    },
    {
      key: 'snapshot',
      x: 50,
      y: 60,
      onPreview: showDataset(AnalyticsType.ANALYTICS_TYPE_VOL_METRICS_24HR),
    },
    {
      key: 'timeframe',
      x: 56,
      y: 12,
      onPreview: showDataset(AnalyticsType.ANALYTICS_TYPE_IMPLIED_VS_REALIZED),
    },
    {
      key: 'compare',
      x: 80,
      y: 12,
      onPreview: showDataset(AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_CONSTANT),
    },
  ]),
  'preset-manager': widget('preset-manager', 'order-preset', 380, [
    { key: 'create', x: 20, y: 44 },
    { key: 'run', x: 50, y: 58 },
    { key: 'manage', x: 88, y: 58 },
  ]),
  'quick-order': widget('quick-order', 'quick-order', 480, [
    { key: 'ticket', x: 55, y: 46 },
    { key: 'ladder', x: 30, y: 80 },
    { key: 'confirm', x: 78, y: 88 },
  ]),
  'one-tap-buy': widget('one-tap-buy', 'one-tap-order', 420, [
    { key: 'activate', x: 86, y: 12 },
    { key: 'tap', x: 13, y: 46 },
    { key: 'edit', x: 13, y: 84 },
  ]),
  spreader: {
    ...widget('spreader', 'spreader', 400, [
      { key: 'pairs', x: 8, y: 55 },
      { key: 'cells', x: 57, y: 55 },
      { key: 'order', x: 90, y: 84 },
    ]),
    previewWidth: 540,
  },
  twap: order(
    'twap',
    { kind: 'order-form', tab: 'TWAP' },
    [
      { key: 'parent', x: 50, y: 19 },
      { key: 'slices', x: 15, y: 73 },
      { key: 'duration', x: 26, y: 90 },
    ],
    AlgoType.ALGO_TYPE_TWAP
  ),
  'advanced-twap': order(
    'advanced-twap',
    { kind: 'order-form', tab: 'Advanced TWAP' },
    [
      { key: 'slices', x: 22, y: 76 },
      { key: 'band', x: 85, y: 37 },
      { key: 'pause', x: 50, y: 80 },
    ],
    AlgoType.ALGO_TYPE_ADVANCED_TWAP
  ),
  'limit-chase': order(
    'limit-chase',
    { kind: 'order-form', tab: 'Limit Chase' },
    [
      { key: 'rest', x: 14, y: 67 },
      { key: 'chase', x: 50, y: 47 },
      { key: 'fill', x: 86, y: 37 },
    ],
    AlgoType.ALGO_TYPE_LIMIT_CHASE
  ),
  rfq: order(
    'rfq',
    { kind: 'order-form', tab: 'RFQ' },
    [
      { key: 'request', x: 18, y: 50 },
      { key: 'makers', x: 82, y: 23 },
      { key: 'execute', x: 82, y: 50 },
    ],
    AlgoType.ALGO_TYPE_RFQ
  ),
  'spreader-algo': order(
    'spreader-algo',
    { kind: 'order-form', spreader: true },
    [
      { key: 'quoting', x: 26, y: 49 },
      { key: 'taking', x: 74, y: 49 },
      { key: 'slice', x: 50, y: 37 },
    ],
    AlgoType.ALGO_TYPE_SPREADER
  ),
  // The arb builder is also a page of its own; from the hub its tour runs there.
  arb: {
    ...order(
      'arb',
      { kind: 'arb' },
      [
        // Leg 1 first, on its own: "+" to pick what it trades (the picker spans every
        // exchange and fills in an account there), then that account — where leg 1 trades,
        // and leg 1 is the reference every size is in. Run on the page, each step waits.
        {
          key: 'leg1Symbol',
          x: 10,
          y: 20,
          waitFor: '[data-arb-leg="leg1"][data-has-symbol="true"]',
          advanceOnDone: true,
        },
        {
          key: 'leg1Account',
          x: 20,
          y: 30,
          waitFor: '[data-tutorial="leg1Account"][data-selected="true"]',
        },
        {
          key: 'legs',
          x: 70,
          y: 40,
          waitFor: '[data-arb-leg="leg2"][data-has-symbol="true"]',
        },
        { key: 'spread', x: 50, y: 60 },
        { key: 'guards', x: 76, y: 75 },
        { key: 'analysis', x: 40, y: 42 },
        {
          key: 'fundingUnits',
          x: 55,
          y: 42,
          waitFor: '[data-tutorial="fundingUnits"][data-funding-unit="apr"]',
        },
        { key: 'backtest', x: 70, y: 42 },
        { key: 'venueFunding', x: 50, y: 85, fallbackKey: 'analysisSections' },
      ],
      AlgoType.ALGO_TYPE_CROSS_EXCHANGE_ARB
    ),
    route: '/trade/arbitrage',
  },
  screener: page('screener', '/trade/screener', ['scope', 'venues', 'search']),
  hedge: {
    ...page('hedge', '/trade/hedge', [
      'target',
      'priceRange',
      'band',
      'mode',
      'conflicts',
      'start',
    ]),
    steps: [
      { key: 'target', x: 70, y: 15 },
      { key: 'priceRange', x: 30, y: 15 },
      { key: 'band', x: 40, y: 50 },
      { key: 'mode', x: 75, y: 60 },
      { key: 'conflicts', x: 75, y: 75, fallbackKey: 'target' },
      { key: 'start', x: 75, y: 90 },
    ],
  },
  assistant: page('assistant', '/trade/assistant', ['ask', 'ideas', 'history']),
  'risk-slides-page': {
    ...page('risk-slides-page', '/trade/risk-slides', [
      'setup',
      'pick',
      'build',
      'run',
      'view',
      'slots',
    ]),
    gate: { feature: BillingFeature.RiskSimulation },
  },
}

export function isTutorialId(value: string): value is TutorialId {
  return value in TUTORIALS
}
